import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import { Router } from '@angular/router';
import { routeIsValidForNonAuthenticatedUser } from '@oneapp/main/core/utils/oa.utils';
import { ResetState } from './state.actions';

const noSnackbarRoutes = [
    '/login'
];

/** Gateway / upstream timeouts that often arrive with an empty or non-API body. */
const TIMEOUT_HTTP_STATUSES = [502, 503, 504];

export interface OaError {
    name: string;
    message: string;
    status: number;
    originalMessage?: string;
}

function isContainedInArray(value: string, arr: string[]) {
    return arr.some(s => value.includes(s));
}

/**
 * Reads a human-readable message from an HttpErrorResponse body.
 * Bodies may be a string, `{ message }`, null, or an empty object (common for 502/504).
 */
function extractHttpErrorMessage(error: HttpErrorResponse): string | undefined {
    if (typeof error.error === 'string') {
        return error.error || undefined;
    }

    const message = error.error?.message;
    return typeof message === 'string' && message.length > 0 ? message : undefined;
}

export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(
        private _snackBar: MatSnackBar,
        private _zone: NgZone,
        private _translate: TranslateService,
        private _store: Store,
        private _router: Router
    ) { }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request)
            .pipe(
                // eslint-disable-next-line complexity
                catchError((error: HttpErrorResponse) => {
                    const rawMessage = extractHttpErrorMessage(error);
                    const transformedError: OaError = {
                        name: error?.statusText,
                        message: rawMessage ?? this._translate.instant('COMMON.WARNINGS.AN_ERROR_OCCURRED'),
                        status: error.status,
                        originalMessage: rawMessage
                    };

                    if (this._router.url === '/' && routeIsValidForNonAuthenticatedUser(window.location.href)) {
                        // router is not yet inialized when this error has occurred (e.g. happens when user info can't be fetched from user initializer)
                        // need to use window to access current URL instead of router
                        return throwError(() => transformedError);
                    }

                    // 502/503/504 often have no usable API message (Heroku, proxies, gateways)
                    if (TIMEOUT_HTTP_STATUSES.includes(error.status)) {
                        transformedError.message = this._translate.instant('COMMON.WARNINGS.503');
                    }

                    if (error.status === 500) {
                        this._zone.run(() => {
                            if (!isContainedInArray(request.url, noSnackbarRoutes)) {
                                this._snackBar.open(
                                    `${this._translate.instant('COMMON.WARNINGS.AN_ERROR_OCCURRED')}: ${transformedError.message}`,
                                    this._translate.instant('COMMON.WARNINGS.DISMISS'),
                                    {
                                        duration: 4000,
                                        panelClass: ['mat-toolbar', 'mat-warn']
                                    }
                                );
                            }
                        });
                    }

                    // if you get a 401, it means that you are unauthorized (logged out) so you should be redirected to the login page
                    if (error.status === 401 && !routeIsValidForNonAuthenticatedUser(this._router.url)) {
                        this._store.dispatch([
                            new ResetState(),
                            new Navigate(['/login'])
                        ]);
                    }

                    return throwError(() => transformedError);
                })
            );
    }
}
